/**
 * Pure helpers for `kind: 'lookup'` columns: compound-id parsing/building,
 * saved-id reconstruction, and target-property resolution status.
 */
import type { AssociationType } from '../../core/SchemaClient';
import type { ColumnConfig } from '../../core/types';
import type { AssociationMatcher } from '../../hooks/useAssociationMatchers';
import type { PropertyTypeSchema } from '../../hooks/usePropertySchema';
import type { EntityTypeSchemaLike } from '../../utils/associationMatching';
import {
  resolveAssociationTargetType,
  resolveAssociationTargetTypes,
} from '../../utils/associationMatching';

export const LOOKUP_COLUMN_NAME_SEPARATOR = '.';

const SORTABLE_LOOKUP_CARDINALITIES = new Set(['many_to_one', 'one_to_one']);

/**
 * Resolve a lookup association's concrete target entity type, EXCLUDING
 * `*_timeline` alias object types from the candidate set.
 *
 * A `*_timeline` entity type name aliases a real entity type's event log and is
 * not a valid user-facing object-type identifier. For a kind-based association
 * (`target_entity_kind_name` set, no concrete `target_entity_type_name`),
 * `resolveAssociationTargetType` picks the first object type whose `entity_kind`
 * matches — and a timeline alias can share that kind, so without this exclusion
 * a kind-based lookup could resolve *through* the timeline alias and index the
 * wrong schema (surfacing a header label / degradation decision from the wrong
 * type). Filtering timeline names keeps the header label, the add-column picker,
 * and the degradation check all resolving to the same real entity type. Mirrors
 * the `!endsWith('_timeline')` exclusion used across the codebase (useObjectTypes,
 * isValidEntityTypeName, buildCustomEntityConfig, linkPickerFilters).
 *
 * Type-based associations are unaffected: `resolveAssociationTargetType` returns
 * their `target_entity_type_name` directly without consulting `objectTypes`.
 */
export function resolveLookupTargetType(
  associationType: Parameters<typeof resolveAssociationTargetType>[0],
  objectTypes: EntityTypeSchemaLike[] | undefined
): string {
  const resolved = resolveAssociationTargetType(
    associationType,
    objectTypes?.filter(t => !t.entity_type_name.endsWith('_timeline'))
  );
  // Kind-based associations already exclude `*_timeline` aliases from the
  // candidate scan above, but a TYPE-based association can name a `*_timeline`
  // target directly (`resolveAssociationTargetType` returns
  // `target_entity_type_name` verbatim). A timeline name aliases an entity
  // type's event log and is never a valid user-facing object type, so reject it
  // here too: resolving to '' (unresolved) means the header label falls back to
  // the synthesized property name, the column degrades, and it is never offered
  // or sorted — instead of surfacing a bogus header from the timeline schema.
  if (resolved.endsWith('_timeline')) {
    return '';
  }
  return resolved;
}

/**
 * Resolve ALL concrete target entity types a lookup association can point at,
 * EXCLUDING `*_timeline` alias object types (see `resolveLookupTargetType` for
 * why timeline aliases are never valid user-facing targets).
 *
 * A type-based association yields exactly one target; a kind-based association
 * can yield several (every object type whose kind matches, minus exclusions).
 * The count matters for sortability: the backend only sorts a compound lookup
 * when the target property is declared on EXACTLY ONE target type
 * (`lookup_sort_unsupported_reason` — ambiguous multi-target properties are
 * rejected). Callers that need that uniqueness check must enumerate every
 * eligible target, not just the first — that is what this function is for.
 */
export function resolveLookupTargetTypes(
  associationType: Parameters<typeof resolveAssociationTargetTypes>[0],
  objectTypes: EntityTypeSchemaLike[] | undefined
): string[] {
  return resolveAssociationTargetTypes(
    associationType,
    objectTypes?.filter(t => !t.entity_type_name.endsWith('_timeline'))
  ).filter(t => !t.endsWith('_timeline'));
}

export interface LookupColumnOption {
  /** Compound column id `<associationTypeName>.<targetPropertyName>`. */
  id: string;
  /** Flat, self-describing label: `"<association> → <target property>"`. */
  label: string;
  /** Target property schema; drives the option icon at the call site. */
  propertySchema: PropertyTypeSchema;
}

export function buildLookupColumnName(
  associationTypeName: string,
  targetPropertyName: string
): string {
  return `${associationTypeName}${LOOKUP_COLUMN_NAME_SEPARATOR}${targetPropertyName}`;
}

export function parseLookupColumnName(
  name: string
): { associationTypeName: string; targetPropertyName: string } | null {
  const separatorIndex = name.indexOf(LOOKUP_COLUMN_NAME_SEPARATOR);
  if (separatorIndex === -1) {
    return null;
  }
  const associationTypeName = name.slice(0, separatorIndex);
  const targetPropertyName = name.slice(
    separatorIndex + LOOKUP_COLUMN_NAME_SEPARATOR.length
  );
  if (!associationTypeName || !targetPropertyName) {
    return null;
  }
  return { associationTypeName, targetPropertyName };
}

export function getLookupColumnParts(
  column: ColumnConfig
): { associationTypeName: string; targetPropertyName: string } | null {
  if (column.kind !== 'lookup') {
    return null;
  }
  if (column.associationTypeName && column.targetPropertyName) {
    return {
      associationTypeName: column.associationTypeName,
      targetPropertyName: column.targetPropertyName,
    };
  }
  return parseLookupColumnName(column.name ?? '');
}

export function reconstructLookupColumnFromSavedId(
  id: string,
  associationTypes: AssociationType[]
): ColumnConfig | null {
  const parts = parseLookupColumnName(id);
  if (!parts) {
    return null;
  }
  const { associationTypeName, targetPropertyName } = parts;
  const isKnownAssociation = associationTypes.some(
    at => at.association_type_name === associationTypeName
  );
  if (!isKnownAssociation) {
    return null;
  }
  return {
    kind: 'lookup',
    name: id,
    associationTypeName,
    targetPropertyName,
  };
}

/**
 * A lookup column is sortable only when its association is single-target
 * (many_to_one / one_to_one), non-transitive, its source matches the entity
 * type currently being viewed, AND the target property itself has a
 * scalar-sortable field type. Many-valued and transitive associations cannot be
 * ordered by the server (ambiguous / multi-hop), and a target property whose
 * field type is unsortable (e.g. textarea, multi_currency, json) has no
 * scalar value for the backend to order by.
 *
 * This mirrors the backend's authoritative check in `validate_sort` /
 * `lookup_sort_unsupported_reason` (`apps/crm_events/serializers.py`,
 * `apps/crm_events/services/property_query_helpers.py`) — cardinality,
 * `transitive_via`, `matches_source`, and target-property scalar sortability.
 * Keep the two in sync: a change to either side's rule should prompt a look at
 * the other.
 *
 * `targetSchemasByType` / `objectTypes` resolve the target property schema;
 * `isFieldTypeSortable` reports whether a field type is scalar-sortable (the
 * same per-field-type flag the property-column sort gate uses). When the target
 * schema has not resolved yet the column fails closed (not sortable) so the UI
 * never exposes a sort control the backend would reject.
 */
export function isLookupColumnSortable(
  column: ColumnConfig,
  associationTypes: AssociationType[],
  entityTypeName: string,
  matchesSource: AssociationMatcher,
  targetSchemasByType: Record<string, Record<string, PropertyTypeSchema>>,
  objectTypes: EntityTypeSchemaLike[] | undefined,
  isFieldTypeSortable: (fieldTypeName: string) => boolean
): boolean {
  const parts = getLookupColumnParts(column);
  if (!parts) {
    return false;
  }
  const assocType = associationTypes.find(
    at => at.association_type_name === parts.associationTypeName
  );
  if (!assocType) {
    return false;
  }
  if (assocType.transitive_via != null) {
    return false;
  }
  if (!matchesSource(assocType, entityTypeName)) {
    return false;
  }
  if (!SORTABLE_LOOKUP_CARDINALITIES.has(assocType.cardinality)) {
    return false;
  }
  // Mirror the backend's ambiguity gate (`lookup_sort_unsupported_reason`): the
  // target property must be declared on EXACTLY ONE eligible target type. A
  // kind-based association can resolve to several target types; if more than one
  // declares the property the backend rejects the sort as ambiguous, and if none
  // do it isn't sortable at all. Inspecting only the first target schema (the
  // old behaviour) let the UI offer a sort the server then rejected. Enumerate
  // every eligible target and require a single declarer — fail closed otherwise,
  // including while target schemas are still loading.
  const declaringTypes = resolveLookupTargetTypes(
    assocType,
    objectTypes
  ).filter(
    targetType => targetSchemasByType[targetType]?.[parts.targetPropertyName]
  );
  if (declaringTypes.length !== 1) {
    return false;
  }
  const targetProp =
    targetSchemasByType[declaringTypes[0]][parts.targetPropertyName];
  return isFieldTypeSortable(targetProp.field_type_name);
}

const LOOKUP_ARROW = '→';

export function buildLookupColumnOptions(params: {
  /** Associations already filtered to the current source entity + exclusions. */
  associations: AssociationType[];
  /** Entity type schemas for kind-based target resolution (may be undefined while loading). */
  objectTypes: EntityTypeSchemaLike[] | undefined;
  /** Target property schemas keyed by entity type name, then property name. */
  targetSchemasByType: Record<string, Record<string, PropertyTypeSchema>>;
  /** Column keys already visible (config + added) so we don't re-offer them. */
  visibleColumnKeys: ReadonlySet<string>;
}): LookupColumnOption[] {
  const { associations, objectTypes, targetSchemasByType, visibleColumnKeys } =
    params;
  const options: LookupColumnOption[] = [];
  for (const assoc of associations) {
    // Fail-closed: never offer a lookup through an association the user cannot
    // read. The per-property `value_read_access` gate below is not enough — the
    // association edge itself is the traversal the backend authorizes, so an
    // `internal` association would surface (and then error out) target
    // properties the user has no path to. Mirrors the target-property gate.
    if (assoc.value_read_access !== 'all') {
      continue;
    }
    // A kind-based association can resolve to SEVERAL concrete target types; a
    // type-based one resolves to exactly one. Enumerate every eligible target,
    // not just the first — the first-target-only approach both offered
    // properties that don't exist on the other targets AND missed the fact that
    // an offerable property must exist on ALL of them.
    const targetTypes = resolveLookupTargetTypes(assoc, objectTypes);
    if (targetTypes.length === 0) {
      continue; // unresolved (e.g. object types still loading) — skip, avoids flicker
    }
    const propsMaps = targetTypes.map(t => targetSchemasByType[t]);
    if (propsMaps.some(m => m === undefined)) {
      continue; // some target schema not loaded yet — can't compute intersection
    }
    // INTERSECTION, not union: the list `/query` backend rejects a lookup whose
    // target property is not declared on EVERY resolved target type of a
    // kind-based association (CrmQuerySerializer.validate →
    // "Unknown properties for target type ... on association ...", HTTP 400).
    // So only offer a property present on all targets — offering a
    // union/first-target property would let the user pick a column the very
    // next list request 400s on. Iterating the first target's props and
    // requiring presence on the rest yields exactly that intersection (any
    // intersection member is, by definition, on the first target too).
    const [firstMap, ...restMaps] = propsMaps as Record<
      string,
      PropertyTypeSchema
    >[];
    const assocLabel = assoc.label || assoc.association_type_name;
    for (const prop of Object.values(firstMap)) {
      const propertyName = prop.property_type_name;
      // The target's display name duplicates the association name-chip; exclude
      // it, mirroring the source-object exclusion of ahoy_display_name.
      if (propertyName === 'ahoy_display_name') {
        continue;
      }
      // Must be declared on every resolved target (intersection); otherwise the
      // backend rejects the query for a heterogeneous kind-based association.
      if (!restMaps.every(m => propertyName in m)) {
        continue;
      }
      // Fail-closed: never offer a value the user cannot read. Exclude if the
      // property is internal-gated on ANY target type, not just the first.
      if (
        propsMaps.some(m => m?.[propertyName]?.value_read_access === 'internal')
      ) {
        continue;
      }
      const id = buildLookupColumnName(
        assoc.association_type_name,
        propertyName
      );
      if (visibleColumnKeys.has(id)) {
        continue; // this exact lookup column is already visible
      }
      options.push({
        id,
        label: `${assocLabel} ${LOOKUP_ARROW} ${prop.label || propertyName}`,
        propertySchema: prop,
      });
    }
  }
  return options;
}

export function isLookupColumnTargetResolved(
  column: ColumnConfig,
  associationTypes: AssociationType[],
  schemasByType: Record<string, Record<string, PropertyTypeSchema>>,
  objectTypes?: EntityTypeSchemaLike[],
  // Whether `objectTypes` reflects a SETTLED fetch (React Query `isFetched`)
  // rather than stale/empty `initialData` still being refetched. Defaults to
  // `true` so callers that only pass warm, non-empty object types — e.g. the
  // prefetch narrower, which guards on `objectTypes?.length` — keep the strict
  // behaviour. The list page threads the real fetched flag so a still-loading
  // schema can never false-drop a valid saved column.
  objectTypesLoaded = true,
  // Whether `associationTypes` reflects a SETTLED fetch (React Query
  // `isFetched`). Defaults to `true` so existing callers (e.g. the prefetch
  // narrower) keep strict behaviour. The list page threads the real fetched
  // flag so a still-loading association list never false-drops a valid saved
  // lookup column mid-load (which would omit its values from the first
  // `/query` and cause a visible flicker/refetch once the schema arrives).
  associationTypesLoaded = true
): boolean {
  const parts = getLookupColumnParts(column);
  if (!parts) {
    return false;
  }
  const assocType = associationTypes.find(
    at => at.association_type_name === parts.associationTypeName
  );
  if (!assocType) {
    // Lenient while the association-types fetch is still in flight: an empty or
    // stale `initialData` list must not drop a valid saved lookup. Drop only
    // once the fetch has SETTLED and the association is genuinely gone.
    return !associationTypesLoaded;
  }
  // Enumerate EVERY resolved target type, not just the first. A kind-based
  // association can point at several concrete target types; the backend list
  // `/query` rejects a lookup whose target property is not declared on EVERY
  // one of them ("Unknown properties for target type ...", HTTP 400). Checking
  // only the first target (the old behaviour) let a saved lookup stay visible
  // when the property was present on the first target but missing on another
  // eligible one — so the very next list query hard-failed for the user.
  const targetTypes = resolveLookupTargetTypes(assocType, objectTypes);
  if (targetTypes.length === 0) {
    // No concrete target resolved. This is ambiguous: the object types may
    // still be loading, OR a kind-based association's target kind resolves to
    // no concrete entity type at all. Only a GENUINE non-resolution should drop
    // the column — so stay lenient (keep) while object types are absent OR the
    // fetch has not settled yet (`objectTypesLoaded === false`, e.g. an
    // empty/stale `initialData` array React Query is still refetching). Degrade
    // only once a settled fetch still yields no concrete target.
    return objectTypes === undefined || !objectTypesLoaded;
  }
  // Require the property on every resolved target (intersection), matching the
  // add-column picker (`buildLookupColumnOptions`) and the backend validation.
  for (const targetType of targetTypes) {
    const map = schemasByType[targetType];
    if (map === undefined) {
      // Target schema not loaded yet — stay lenient (keep) to avoid
      // false-dropping a valid saved column mid-load.
      return true;
    }
    if (!(parts.targetPropertyName in map)) {
      return false;
    }
  }
  return true;
}
